"use client"

import { useEffect } from "react"
import { useRouter } from "@/lib/navigation"
import { useAuth } from "@/contexts/auth-context"
import { useToast } from "@/hooks/use-toast"
import { setAuthFailureHandler } from "@/lib/api-config"
import NotLoggedIn from "@/components/not-logged-in"
import { SessionRetry } from "@/components/session-retry"

interface RequireAuthProps {
  role?: "user" | "admin"
  children: React.ReactNode
}

function Forbidden() {
  return (
    <div
      className="flex min-h-[100dvh] w-full items-center justify-center"
      style={{
        backgroundColor: "var(--cream-50)",
        backgroundImage: "radial-gradient(var(--ember-300) 1.5px, transparent 1.5px)",
        backgroundSize: "20px 20px",
      }}
    >
      <div className="text-center">
        <div className="mb-4 font-medium" style={{ color: "var(--ember-600)" }}>
          无权限访问此页面
        </div>
        <button
          type="button"
          className="rounded-lg px-4 py-2 text-sm font-medium"
          style={{ background: "var(--leaf-500)", color: "white" }}
          onClick={() => (window.location.href = "/")}
        >
          返回登录
        </button>
      </div>
    </div>
  )
}

export function RequireAuth({ role, children }: RequireAuthProps) {
  const router = useRouter()
  const { toast } = useToast()
  const { token, userType, isLoading, isAuthenticated, logout, sessionError, refreshSession } = useAuth()

  useEffect(() => {
    setAuthFailureHandler((status, message) => {
      if (status === 401) {
        logout()
        toast({
          variant: "destructive",
          title: "登录已失效",
          description: message,
        })
        router.replace("/")
        return
      }
      toast({
        variant: "destructive",
        title: "无权限访问",
        description: message,
      })
    })
    return () => setAuthFailureHandler(null)
  }, [logout, router, toast])

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <div className="text-sm text-muted-foreground">加载中...</div>
      </div>
    )
  }

  if (sessionError) {
    return <SessionRetry message={sessionError} onRetry={refreshSession} onLogout={logout} />
  }

  if (!isAuthenticated || !token) {
    return <NotLoggedIn />
  }

  if (role && userType !== role) {
    return <Forbidden />
  }

  return <>{children}</>
}
